import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  FormGroup,
  Table,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  getCashLedgers,
  saveCashLedger,
  getCashLedgerById,
} from "../../components/Api/GmsCashLedger";
import { getBanks } from "../../components/Api/GmsBank";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getCostHeads } from "../../components/Api/GmsCostHead";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import { Edit, Eye, PlusCircle, MinusCircle, Wallet, History, ClipboardList, BarChart3, AlertCircle } from "lucide-react";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const COLORS = ['#556ee6', '#34c38f', '#f46a6a', '#50a5f1', '#f1b44c', '#8b5cf6'];

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

const defaultEntryForm = {
  Amount: "",
  TransactorType: null,
  TransactorId: null,
  CostHeadId: null,
  Notes: "",
};

export default function PettyCashDashboard() {
  // Session & Permissions
  const session = useMemo(() => getUserSession(), []);
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pagePath = '/GmsPettyCashReceipts';
  const hasPermission = hasPagePermissionWithAdmin(permissions, pagePath);
  const isViewOnly = hasViewOnlyPermission(permissions, pagePath);
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, pagePath);

  // Filters State
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Data State
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [summary, setSummary] = useState({
    opening: 0,
    totalIn: 0,
    totalOut: 0,
    closing: 0,
  });

  // Forms State
  const [cashInForm, setCashInForm] = useState(defaultEntryForm);
  const [cashOutForm, setCashOutForm] = useState(defaultEntryForm);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Transactor Data State
  const [banks, setBanks] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherPayees, setOtherPayees] = useState([]);
  const [inTransactorOptions, setInTransactorOptions] = useState([]);
  const [outTransactorOptions, setOutTransactorOptions] = useState([]);
  const [inBankAccounts, setInBankAccounts] = useState([]);
  const [outBankAccounts, setOutBankAccounts] = useState([]);
  const [inSelectedBank, setInSelectedBank] = useState(null);
  const [outSelectedBank, setOutSelectedBank] = useState(null);

  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(defaultEntryForm);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editIsReceipt, setEditIsReceipt] = useState(false);
  const [editSelectedBank, setEditSelectedBank] = useState(null);
  const [editTransactorOptions, setEditTransactorOptions] = useState([]);

  // Transactor types
  const transactorTypeOptions = [
    { value: 'ba', label: 'Banks' },
    { value: 'em', label: 'Employee' },
    { value: 'su', label: 'Supplier' },
    { value: 'ot', label: 'Others' },
  ];

  const formatCurrency = (amount) => {
    const currencyCode = localStorage.getItem("currencyCode") || "AED";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // --- Initial Data Loading ---
  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [banksRes, costHeadsRes, suppliersRes, otherPayeesRes, businessesRes] = await Promise.all([
        getBanks({}),
        getCostHeads({}),
        getSuppliers({}),
        getOtherPayees({}),
        getBusinesses({ business_Active: true }),
      ]);

      const banksArray = Array.isArray(banksRes?.banks) ? banksRes.banks : (Array.isArray(banksRes) ? banksRes : []);
      setBanks(banksArray);
      setCostHeads(Array.isArray(costHeadsRes) ? costHeadsRes : []);
      setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : []);
      setOtherPayees(Array.isArray(otherPayeesRes) ? otherPayeesRes : []);
      
      const businessesArray = Array.isArray(businessesRes) ? businessesRes : (businessesRes?.data || []);
      setBusinesses(businessesArray);

      // Auto-select business if session exists
      if (session.businessId) {
        const found = businessesArray.find(b => (b.business_Id || b.businessId) === session.businessId);
        if (found) {
          const opt = { value: session.businessId, label: found.business_NameEn || found.business_Name || "" };
          setSelectedBusiness(opt);
          loadBranches(session.businessId, opt);
        }
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async (businessId, businessOpt) => {
    try {
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setBranches(branchData);

      if (session.branchId) {
        const found = branchData.find(b => (b.branch_Id || b.branchId) === session.branchId);
        if (found) {
          setSelectedBranch({
            value: session.branchId,
            label: `${found.branch_NameEn || found.branch_Name || ''} - ${found.branch_NameAr || ''}`.trim()
          });
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadEmployees = useCallback(async (businessId, branchId) => {
    try {
      const filter = {
        Employee_Business_Id: businessId,
        Employee_Branch_Id: branchId
      };
      const data = await getEmployees(filter);
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  }, []);

  // --- Core Dashboard Logic ---
  const fetchDashboardData = useCallback(async () => {
    if (!selectedBusiness || !selectedDate) return;

    setLoading(true);
    try {
      const busId = selectedBusiness.value;
      const braId = selectedBranch?.value || null;
      const dateStr = formatDateForAPI(selectedDate, false);

      // 1. Check Day Closing for Permission
      const checkRes = await checkDayClosing({
        transactionDate: dateStr,
        businessId: busId,
        branchId: braId,
        checkPreviousDays: true
      });
      setIsReadOnly(checkRes.isClosed || !!checkRes.firstUnclosedDate);

      // 2. Fetch Opening Balance (Sum all entries before selected date)
      // Since no dedicated "Get Balance" API, we fetch entries up to yesterday
      const yesterdayEnd = new Date(selectedDate);
      yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
      yesterdayEnd.setHours(23, 59, 59, 999);
      
      const prevFilter = {
        BusinessId: busId,
        BranchId: braId,
        ToDate: formatDateForAPI(yesterdayEnd, true),
        PageSize: 10000 // High number to get most entries for calculation
      };
      
      const prevRes = await getCashLedgers(prevFilter);
      const prevEntries = prevRes.cashLedgers || [];
      const openingBal = prevEntries.reduce((sum, e) => {
        const debit = parseFloat(e.debit || e.Debit || 0);
        const credit = parseFloat(e.credit || e.Credit || 0);
        return sum + debit - credit;
      }, 0);

      // 3. Fetch Today's Entries
      const fromOfToday = new Date(selectedDate);
      fromOfToday.setHours(0, 0, 0, 0);
      
      const toOfToday = new Date(selectedDate);
      toOfToday.setHours(23, 59, 59, 999);

      const todayFilter = {
        BusinessId: busId,
        BranchId: braId,
        FromDate: formatDateForAPI(fromOfToday, true),
        ToDate: formatDateForAPI(toOfToday, true),
        PageSize: 1000
      };
      const todayRes = await getCashLedgers(todayFilter);
      const todayEntries = todayRes.cashLedgers || [];
      setLedgerEntries(todayEntries);

      // 4. Calculate Summary
      const totalIn = todayEntries.reduce((sum, e) => sum + parseFloat(e.debit || e.Debit || 0), 0);
      const totalOut = todayEntries.reduce((sum, e) => sum + parseFloat(e.credit || e.Credit || 0), 0);

      setSummary({
        opening: openingBal,
        totalIn: totalIn,
        totalOut: totalOut,
        closing: openingBal + totalIn - totalOut
      });

      // 5. Load employees for this context
      loadEmployees(busId, braId);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, selectedBranch, selectedDate, loadEmployees]);

  const expenseChartData = useMemo(() => {
    const data = {};
    ledgerEntries.forEach(entry => {
      const credit = parseFloat(entry.credit || entry.Credit || 0);
      if (credit > 0) {
        const typeKey = entry.transactorType || entry.TransactorType;
        const typeLabel = transactorTypeOptions.find(t => t.value === typeKey)?.label || "Other";
        data[typeLabel] = (data[typeLabel] || 0) + credit;
      }
    });

    return Object.keys(data).map(key => ({
      name: key,
      value: data[key]
    })).filter(item => item.value > 0);
  }, [ledgerEntries]);

  const receiptChartData = useMemo(() => {
    const data = {};
    ledgerEntries.forEach(entry => {
      const debit = parseFloat(entry.debit || entry.Debit || 0);
      if (debit > 0) {
        const typeKey = entry.transactorType || entry.TransactorType;
        const typeLabel = transactorTypeOptions.find(t => t.value === typeKey)?.label || "Other";
        data[typeLabel] = (data[typeLabel] || 0) + debit;
      }
    });

    return Object.keys(data).map(key => ({
      name: key,
      value: data[key]
    })).filter(item => item.value > 0);
  }, [ledgerEntries]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchDashboardData();
    }
  }, [selectedBusiness, selectedBranch, selectedDate, fetchDashboardData]);

  // --- Selection Handlers ---
  const handleBusinessChange = (opt) => {
    setSelectedBusiness(opt);
    setSelectedBranch(null);
    if (opt) loadBranches(opt.value, opt);
  };

  const handleTransactorTypeChange = (type, isReceipt, isEdit = false) => {
    const list = getTransactorOptions(type);
    if (isEdit) {
      setEditForm(prev => ({ ...prev, TransactorType: type, TransactorId: null }));
      setEditTransactorOptions(list);
      setEditSelectedBank(null);
    } else if (isReceipt) {
      setCashInForm(prev => ({ ...prev, TransactorType: type, TransactorId: null }));
      setInTransactorOptions(list);
      setInSelectedBank(null);
    } else {
      setCashOutForm(prev => ({ ...prev, TransactorType: type, TransactorId: null }));
      setOutTransactorOptions(list);
      setOutSelectedBank(null);
    }
  };

  const getTransactorOptions = (type) => {
    switch (type) {
      case 'ba':
        return banks.map(b => ({
          value: b.bankId || b.bank_Id,
          label: b.bankNameEn || b.bank_NameEn || b.bank_Name || ''
        }));
      case 'em':
        return employees.map(e => ({
          value: e.employee_Id || e.employeeId,
          label: `${e.employee_Code || ""} - ${e.employee_NameEn || e.employeeNameEn || ""}`.trim()
        }));
      case 'su':
        return suppliers.map(s => ({
          value: s.supplier_Id || s.supplierId,
          label: s.supplier_NameEn || s.supplierNameEn || s.supplier_Name || ""
        }));
      case 'ot':
        return otherPayees.map(o => ({
          value: o.otherPayee_Id || o.otherPayeeId,
          label: o.otherPayee_Name || o.otherPayeeName || ""
        }));
      default:
        return [];
    }
  };

  const handleBankChange = async (bankId, isReceipt, isEdit = false) => {
    try {
      const res = await getBankAccounts({ bankId });
      const accounts = Array.isArray(res?.bankAccounts) ? res.bankAccounts : (Array.isArray(res) ? res : []);
      const opts = accounts.map(a => ({
        value: a.bankAccountId || a.bankAccount_Id,
        label: `${a.accountHolderName || ""} - ${a.accountNumber || ""}`.trim()
      }));

      if (isEdit) {
        setEditTransactorOptions(opts);
      } else if (isReceipt) {
        setInTransactorOptions(opts);
      } else {
        setOutTransactorOptions(opts);
      }
    } catch (error) {
      console.error("Error loading bank accounts:", error);
    }
  };

  // --- Save Logic ---
  const handleSave = async (isReceipt, formToSave) => {
    if (saveLoading) return;

    // Validation
    const busId = selectedBusiness?.value || session.businessId;
    const braId = selectedBranch?.value || session.branchId;

    if (!busId) return Swal.fire("Error", "Company is mandatory", "error");
    if (!formToSave.Amount || parseFloat(formToSave.Amount) <= 0) return Swal.fire("Error", "Enter valid amount", "error");
    if (!formToSave.TransactorType || !formToSave.TransactorId) return Swal.fire("Error", "Select transactor", "error");

    setSaveLoading(true);
    try {
      // Re-check day closing right before save
      const checkRes = await checkDayClosing({
        transactionDate: formatDateForAPI(selectedDate, false),
        businessId: busId,
        branchId: braId,
        checkPreviousDays: true
      });

      if (checkRes.isClosed) return Swal.fire("Error", "Day is closed", "error");
      if (checkRes.firstUnclosedDate) {
        const d = formatDateForDisplay(new Date(checkRes.firstUnclosedDate), 'date');
        return Swal.fire("Error", `Close previous day (${d}) first`, "error");
      }

      const payload = {
        SlNo: 0,
        Date: formatDateForAPI(selectedDate, true), 
        Debit: isReceipt ? parseFloat(formToSave.Amount) : null,
        Credit: !isReceipt ? parseFloat(formToSave.Amount) : null,
        Notes: formToSave.Notes || null,
        TransactorType: formToSave.TransactorType,
        TransactorId: formToSave.TransactorId,
        CostHeadId: formToSave.CostHeadId || null,
        BusinessId: busId,
        BranchId: braId,
        CreatedBy: session.userId,
      };

      const result = await saveCashLedger(payload);
      if (result.isSuccess) {
        Swal.fire("Success", "Saved successfully", "success");
        if (isReceipt) {
          setCashInForm(defaultEntryForm);
          setInSelectedBank(null);
        } else {
          setCashOutForm(defaultEntryForm);
          setOutSelectedBank(null);
        }
        fetchDashboardData();
      } else {
        Swal.fire("Error", result.message || "Failed to save", "error");
      }
    } catch (error) {
      console.error("Error saving ledger:", error);
      Swal.fire("Error", "An error occurred", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEditClick = async (entry) => {
    try {
      const data = await getCashLedgerById(entry.cashLedgerID || entry.CashLedgerID);
      if (!data) return;

      const isReceipt = (data.debit || data.Debit || 0) > 0;
      setEditIsReceipt(isReceipt);
      setEditingEntryId(data.cashLedgerID || data.CashLedgerID);
      
      const amt = isReceipt ? (data.debit || data.Debit) : (data.credit || data.Credit);
      
      setEditForm({
        Amount: amt,
        TransactorType: data.transactorType || data.TransactorType,
        TransactorId: data.transactorId || data.TransactorId,
        CostHeadId: data.costHeadId || data.CostHeadId,
        Notes: data.notes || data.Notes || "",
      });

      // Handle bank accounts if type is bank
      if ((data.transactorType || data.TransactorType) === 'ba') {
        // Need to find which bank this account belongs to
        // For now, reload accounts if possible. Usually the TransactorId is the BankAccountId.
        handleBankChange(null, false, true); // This is a bit tricky without knowing bankId here
      } else {
        setEditTransactorOptions(getTransactorOptions(data.transactorType || data.TransactorType));
      }

      setShowEditModal(true);
    } catch (err) {
      Swal.fire("Error", "Failed to load entry", "error");
    }
  };

  const handleUpdate = async () => {
    if (saveLoading) return;
    setSaveLoading(true);
    try {
        const payload = {
            CashLedgerID: editingEntryId,
            Date: formatDateForAPI(selectedDate, true), // Preserve original date but keep time format
            Debit: editIsReceipt ? parseFloat(editForm.Amount) : null,
            Credit: !editIsReceipt ? parseFloat(editForm.Amount) : null,
            Notes: editForm.Notes,
            TransactorType: editForm.TransactorType,
            TransactorId: editForm.TransactorId,
            CostHeadId: editForm.CostHeadId,
            BusinessId: selectedBusiness.value,
            BranchId: selectedBranch?.value || null,
            ModifiedBy: session.userId,
        };
        const res = await saveCashLedger(payload);
        if (res.isSuccess) {
            Swal.fire("Success", "Updated successfully", "success");
            setShowEditModal(false);
            fetchDashboardData();
        } else {
            Swal.fire("Error", res.message, "error");
        }
    } catch (err) {
        Swal.fire("Error", "Update failed", "error");
    } finally {
        setSaveLoading(false);
    }
  };

  // --- Helpers ---
  
  const getTransactorName = (type, id) => {
    switch (type) {
      case 'ba': return "Bank Account"; // Mapping bank account name requires complex lookup
      case 'em': {
        const e = employees.find(x => (x.employee_Id || x.employeeId) == id);
        return e ? (e.employee_NameEn || e.employeeNameEn) : id;
      }
      case 'su': {
        const s = suppliers.find(x => (x.supplier_Id || x.supplierId) == id);
        return s ? (s.supplier_NameEn || s.supplierNameEn) : id;
      }
      case 'ot': {
        const o = otherPayees.find(x => (x.otherPayee_Id || x.otherPayeeId) == id);
        return o ? (o.otherPayee_Name || o.otherPayeeName) : id;
      }
      default: return "-";
    }
  };

  const getCostHeadName = (id) => {
    const ch = costHeads.find(x => (x.costHead_Id || x.CostHead_Id) == id);
    return ch ? (ch.costHead_Name || ch.CostHead_Name) : "-";
  };

  if (!hasPermission) return null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Petty Cash" breadcrumbItem="Dashboard" />

          {/* Selection Header */}
          <Card className="mb-4 shadow-sm border-0">
            <CardBody className="p-4">
              <Row className="align-items-end">
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="fw-bold">Company <span className="text-danger">*</span></Label>
                    <Select
                      value={selectedBusiness}
                      onChange={handleBusinessChange}
                      options={businesses.map(b => ({ value: b.business_Id || b.businessId, label: b.business_NameEn || b.business_Name || "" }))}
                      isDisabled={!!session.businessId}
                      styles={selectStyles}
                      placeholder="Select Company"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="fw-bold">Branch</Label>
                    <Select
                      value={selectedBranch}
                      onChange={setSelectedBranch}
                      options={branches.map(b => ({ value: b.branch_Id || b.branchId, label: `${b.branch_NameEn || b.branch_Name || ""} - ${b.branch_NameAr || ""}` }))}
                      isDisabled={!!session.branchId || !selectedBusiness}
                      styles={selectStyles}
                      isClearable
                      placeholder="Select Branch"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-0">
                    <Label className="fw-bold">Date</Label>
                    <div className="input-group">
                      <DatePicker
                        selected={selectedDate}
                        onChange={setSelectedDate}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        maxDate={new Date()}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {!selectedBusiness || !selectedBranch ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <ClipboardList size={64} className="text-muted opacity-25" />
              </div>
              <h4 className="text-muted">Please select a company and branch to view the dashboard</h4>
            </div>
          ) : (
            <>
              {/* Day Closing Warning Alert */}
              {isReadOnly && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Alert color="warning" className="border-0 shadow-sm d-flex align-items-center" style={{ borderRadius: '12px' }}>
                      <AlertCircle size={24} className="me-3 text-warning" />
                      <div>
                        <h6 className="alert-heading fw-bold mb-1">Transactions Locked</h6>
                        <p className="mb-0 small">Entries for <strong>{formatDateForDisplay(selectedDate, 'date')}</strong> cannot be added or modified because this date has been closed through the Day Closing process.</p>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Reconciliation Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="mini-stats-wid border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-0">
                      <div className="d-flex bg-primary p-3" style={{ color: 'white' }}>
                        <div className="flex-grow-1">
                          <p className="text-white-50 fw-medium mb-1">Opening Balance</p>
                          <h4 className="mb-0 text-white">{formatCurrency(summary.opening)}</h4>
                        </div>
                        <div className="avatar-sm align-self-center mini-stat-icon rounded-circle bg-white-50">
                          <span className="avatar-title text-white">
                            <Wallet size={20} />
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stats-wid border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-0">
                      <div className="d-flex bg-success p-3" style={{ color: 'white' }}>
                        <div className="flex-grow-1">
                          <p className="text-white-50 fw-medium mb-1">Today's Cash In</p>
                          <h4 className="mb-0 text-white">+{formatCurrency(summary.totalIn)}</h4>
                        </div>
                        <div className="avatar-sm align-self-center mini-stat-icon rounded-circle bg-white-50">
                          <span className="avatar-title text-white">
                            <PlusCircle size={20} />
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stats-wid border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-0">
                      <div className="d-flex bg-danger p-3" style={{ color: 'white' }}>
                        <div className="flex-grow-1">
                          <p className="text-white-50 fw-medium mb-1">Today's Cash Out</p>
                          <h4 className="mb-0 text-white">-{formatCurrency(summary.totalOut)}</h4>
                        </div>
                        <div className="avatar-sm align-self-center mini-stat-icon rounded-circle bg-white-50">
                          <span className="avatar-title text-white">
                            <MinusCircle size={20} />
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stats-wid border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-0">
                      <div className="d-flex bg-info p-3" style={{ color: 'white' }}>
                        <div className="flex-grow-1">
                          <p className="text-white-50 fw-medium mb-1">Current Balance</p>
                          <h4 className="mb-0 text-white">{formatCurrency(summary.closing)}</h4>
                        </div>
                        <div className="avatar-sm align-self-center mini-stat-icon rounded-circle bg-white-50">
                          <span className="avatar-title text-white">
                            <History size={20} />
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                {/* Add Cash Column */}
                <Col md={6}>
                  <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                    <CardBody>
                      <div className="d-flex align-items-center mb-4">
                        <div className="bg-soft bg-success p-2 rounded me-3">
                           <PlusCircle className="text-success" size={24} />
                        </div>
                        <h5 className="card-title mb-0">Add Cash (Receipt)</h5>
                      </div>
                      
                      <Form>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Transactor Type <span className="text-danger">*</span></Label>
                              <Select
                                value={transactorTypeOptions.find(t => t.value === cashInForm.TransactorType) || null}
                                onChange={(opt) => handleTransactorTypeChange(opt?.value, true)}
                                options={transactorTypeOptions}
                                styles={selectStyles}
                                isDisabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Transactor Selection <span className="text-danger">*</span></Label>
                              {cashInForm.TransactorType === 'ba' && !inSelectedBank ? (
                                <Select
                                  value={null}
                                  onChange={(opt) => {
                                    setInSelectedBank(opt);
                                    handleBankChange(opt.value, true);
                                  }}
                                  options={banks.map(b => ({ value: b.bankId || b.bank_Id, label: b.bankNameEn || b.bank_NameEn || "" }))}
                                  styles={selectStyles}
                                  placeholder="Select Bank"
                                  isDisabled={isReadOnly || isViewOnly}
                                />
                              ) : (
                                <Select
                                  value={inTransactorOptions.find(o => o.value === cashInForm.TransactorId) || null}
                                  onChange={(opt) => setCashInForm(prev => ({ ...prev, TransactorId: opt?.value }))}
                                  options={inTransactorOptions}
                                  styles={selectStyles}
                                  placeholder="Select Transactor"
                                  isDisabled={isReadOnly || isViewOnly || (cashInForm.TransactorType === 'ba' && !inSelectedBank)}
                                />
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                           <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Amount <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                value={cashInForm.Amount}
                                onChange={(e) => setCashInForm(prev => ({ ...prev, Amount: e.target.value }))}
                                placeholder="0.00"
                                disabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Cost Head</Label>
                              <Select
                                value={costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === cashInForm.CostHeadId) 
                                  ? { value: cashInForm.CostHeadId, label: costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === cashInForm.CostHeadId).costHead_Name } 
                                  : null}
                                onChange={(opt) => setCashInForm(prev => ({ ...prev, CostHeadId: opt?.value }))}
                                options={costHeads.map(c => ({ value: c.costHead_Id || c.CostHead_Id, label: c.costHead_Name || c.CostHead_Name }))}
                                styles={selectStyles}
                                isClearable
                                isDisabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup className="mb-3">
                          <Label>Notes</Label>
                          <Input
                            type="textarea"
                            rows="2"
                            value={cashInForm.Notes}
                            onChange={(e) => setCashInForm(prev => ({ ...prev, Notes: e.target.value }))}
                            placeholder="Optional notes"
                            disabled={isReadOnly || isViewOnly}
                          />
                        </FormGroup>
                        <Button
                          color="success"
                          className="w-100 py-2 d-flex align-items-center justify-content-center"
                          onClick={() => handleSave(true, cashInForm)}
                          disabled={isReadOnly || isViewOnly || saveLoading}
                        >
                          <PlusCircle size={18} className="me-2" />
                          Save Receipt
                        </Button>
                      </Form>
                    </CardBody>
                  </Card>
                </Col>

                {/* Add Expense Column */}
                <Col md={6}>
                  <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                    <CardBody>
                      <div className="d-flex align-items-center mb-4">
                        <div className="bg-soft bg-danger p-2 rounded me-3">
                           <MinusCircle className="text-danger" size={24} />
                        </div>
                        <h5 className="card-title mb-0">Add Expense (Payment)</h5>
                      </div>

                      <Form>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Transactor Type <span className="text-danger">*</span></Label>
                              <Select
                                value={transactorTypeOptions.find(t => t.value === cashOutForm.TransactorType) || null}
                                onChange={(opt) => handleTransactorTypeChange(opt?.value, false)}
                                options={transactorTypeOptions}
                                styles={selectStyles}
                                isDisabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Transactor Selection <span className="text-danger">*</span></Label>
                              {cashOutForm.TransactorType === 'ba' && !outSelectedBank ? (
                                <Select
                                  value={null}
                                  onChange={(opt) => {
                                    setOutSelectedBank(opt);
                                    handleBankChange(opt.value, false);
                                  }}
                                  options={banks.map(b => ({ value: b.bankId || b.bank_Id, label: b.bankNameEn || b.bank_NameEn || "" }))}
                                  styles={selectStyles}
                                  placeholder="Select Bank"
                                  isDisabled={isReadOnly || isViewOnly}
                                />
                              ) : (
                                <Select
                                  value={outTransactorOptions.find(o => o.value === cashOutForm.TransactorId) || null}
                                  onChange={(opt) => setCashOutForm(prev => ({ ...prev, TransactorId: opt?.value }))}
                                  options={outTransactorOptions}
                                  styles={selectStyles}
                                  placeholder="Select Transactor"
                                  isDisabled={isReadOnly || isViewOnly || (cashOutForm.TransactorType === 'ba' && !outSelectedBank)}
                                />
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                           <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Amount <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                value={cashOutForm.Amount}
                                onChange={(e) => setCashOutForm(prev => ({ ...prev, Amount: e.target.value }))}
                                placeholder="0.00"
                                disabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label>Cost Head</Label>
                              <Select
                                value={costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === cashOutForm.CostHeadId) 
                                  ? { value: cashOutForm.CostHeadId, label: costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === cashOutForm.CostHeadId).costHead_Name } 
                                  : null}
                                onChange={(opt) => setCashOutForm(prev => ({ ...prev, CostHeadId: opt?.value }))}
                                options={costHeads.map(c => ({ value: c.costHead_Id || c.CostHead_Id, label: c.costHead_Name || c.CostHead_Name }))}
                                styles={selectStyles}
                                isClearable
                                isDisabled={isReadOnly || isViewOnly}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup className="mb-3">
                          <Label>Notes</Label>
                          <Input
                            type="textarea"
                            rows="2"
                            value={cashOutForm.Notes}
                            onChange={(e) => setCashOutForm(prev => ({ ...prev, Notes: e.target.value }))}
                            placeholder="Optional notes"
                            disabled={isReadOnly || isViewOnly}
                          />
                        </FormGroup>
                        <Button
                          color="danger"
                          className="w-100 py-2 d-flex align-items-center justify-content-center"
                          onClick={() => handleSave(false, cashOutForm)}
                          disabled={isReadOnly || isViewOnly || saveLoading}
                        >
                          <MinusCircle size={18} className="me-2" />
                          Save Expense
                        </Button>
                      </Form>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Ledger & Reconciliation Section */}
              <Row className="mt-4 pb-5">
                {/* Today's Ledger */}
                <Col md={7}>
                  <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center">
                           <div className="bg-soft bg-info p-2 rounded me-3">
                             <ClipboardList className="text-info" size={24} />
                           </div>
                           <h5 className="card-title mb-0">Today's Ledger</h5>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <Table hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '60px' }}>Sr.No</th>
                              <th>Transactor Type</th>
                              <th>Transactor Selection</th>
                              <th className="text-end">Cash In</th>
                              <th className="text-end">Cash Out</th>
                              <th className="text-center" style={{ width: '150px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr><td colSpan="6" className="text-center py-4"><ClipLoader size={30} color="#556ee6" /></td></tr>
                            ) : ledgerEntries.length === 0 ? (
                              <tr><td colSpan="6" className="text-center py-4 text-muted">No transactions recorded for this day</td></tr>
                            ) : (
                              ledgerEntries.map((entry, idx) => (
                                <tr key={entry.cashLedgerID || entry.CashLedgerID}>
                                  <td>{idx + 1}</td>
                                  <td>
                                    <span className="text-secondary fw-medium">
                                      {transactorTypeOptions.find(t => t.value === (entry.transactorType || entry.TransactorType))?.label}
                                    </span>
                                  </td>
                                  <td className="text-primary fw-medium">
                                    {getTransactorName(entry.transactorType || entry.TransactorType, entry.transactorId || entry.TransactorId)}
                                  </td>
                                  <td className="text-end text-success fw-bold">
                                    {(entry.debit || entry.Debit) > 0 ? formatCurrency(entry.debit || entry.Debit) : "-"}
                                  </td>
                                  <td className="text-end text-danger fw-bold">
                                    {(entry.credit || entry.Credit) > 0 ? formatCurrency(entry.credit || entry.Credit) : "-"}
                                  </td>
                                  <td className="text-center">
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="me-2 text-info bg-soft-info border-0"
                                      onClick={() => {
                                        setDetailEntry(entry);
                                        setShowDetailModal(true);
                                      }}
                                      title="View Details"
                                    >
                                      <Eye size={16} />
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="text-primary bg-soft-primary border-0"
                                      onClick={() => handleEditClick(entry)}
                                      disabled={isReadOnly || isViewOnly}
                                      title="Edit Entry"
                                    >
                                      <Edit size={16} />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          {!loading && ledgerEntries.length > 0 && (
                             <tfoot className="table-light fw-bold">
                                <tr>
                                  <td colSpan="3" className="text-end">Day Totals:</td>
                                  <td className="text-end text-success">{formatCurrency(summary.totalIn)}</td>
                                  <td className="text-end text-danger">{formatCurrency(summary.totalOut)}</td>
                                  <td></td>
                                </tr>
                             </tfoot>
                          )}
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Charts Card */}
                <Col md={5}>
                  <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                    <CardBody className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-4">
                        <div className="bg-soft bg-primary p-2 rounded me-3">
                           <BarChart3 className="text-primary" size={24} />
                        </div>
                        <h5 className="card-title mb-0">Today's Cash Flow Breakdown</h5>
                      </div>

                      {ledgerEntries.length === 0 ? (
                        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center p-4">
                           <div className="bg-light rounded-circle p-4 mb-3">
                              <Wallet className="text-muted opacity-50" size={48} />
                           </div>
                           <p className="text-muted">No transactions recorded for today to visualize.</p>
                        </div>
                      ) : (
                        <Row className="flex-grow-1">
                          {/* Receipts Pie */}
                          <Col md={6} className="text-center">
                            <h6 className="text-success small fw-bold mb-2">Today's Receipts</h6>
                            {receiptChartData.length === 0 ? (
                              <div className="h-100 d-flex align-items-center justify-content-center text-muted small py-5">No Receipts</div>
                            ) : (
                              <div style={{ height: "220px" }}>
                                <ResponsiveContainer width="100%" height={220}>
                                  <PieChart>
                                    <Pie
                                      data={receiptChartData}
                                      cx="50%" cy="50%"
                                      innerRadius={40} outerRadius={60}
                                      paddingAngle={5} dataKey="value"
                                    >
                                      {receiptChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            )}
                          </Col>

                          {/* Expenses Pie */}
                          <Col md={6} className="text-center">
                            <h6 className="text-danger small fw-bold mb-2">Today's Expenses</h6>
                            {expenseChartData.length === 0 ? (
                              <div className="h-100 d-flex align-items-center justify-content-center text-muted small py-5">No Expenses</div>
                            ) : (
                              <div style={{ height: "220px" }}>
                                <ResponsiveContainer width="100%" height={220}>
                                  <PieChart>
                                    <Pie
                                      data={expenseChartData}
                                      cx="50%" cy="50%"
                                      innerRadius={40} outerRadius={60}
                                      paddingAngle={5} dataKey="value"
                                    >
                                      {expenseChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            )}
                          </Col>
                        </Row>
                      )}
                      
                      <div className="mt-auto pt-3">
                        <div className="d-flex justify-content-between p-2 mb-2 bg-success rounded-3 small">
                            <span className="text-white">Total Receipts:</span>
                            <span className="fw-bold text-white">+{formatCurrency(summary.totalIn)}</span>
                        </div>
                        <div className="d-flex justify-content-between p-2 bg-danger rounded-3 small">
                            <span className="text-white">Total Expenses:</span>
                            <span className="fw-bold text-white">-{formatCurrency(summary.totalOut)}</span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* Details Modal */}
          <Modal isOpen={showDetailModal} toggle={() => setShowDetailModal(false)} centered>
            <ModalHeader toggle={() => setShowDetailModal(false)} className="bg-light">
              Entry Details
            </ModalHeader>
            <ModalBody className="p-4">
              {detailEntry && (
                <div className="details-list">
                  <Row className="mb-3 border-bottom pb-3">
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Transaction Date</Label>
                      <div className="fw-bold">{formatDateForDisplay(detailEntry.date || detailEntry.Date, 'date')}</div>
                    </Col>
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Type</Label>
                      <div className={`fw-bold ${(detailEntry.debit || detailEntry.Debit) > 0 ? "text-success" : "text-danger"}`}>
                        {(detailEntry.debit || detailEntry.Debit) > 0 ? "Receipt (+)" : "Expense (-)"}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3 border-bottom pb-3">
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Transactor Type</Label>
                      <div className="fw-bold text-secondary">
                        {transactorTypeOptions.find(t => t.value === (detailEntry.transactorType || detailEntry.TransactorType))?.label || "-"}
                      </div>
                    </Col>
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Transactor Name</Label>
                      <div className="fw-bold text-primary">
                        {getTransactorName(detailEntry.transactorType || detailEntry.TransactorType, detailEntry.transactorId || detailEntry.TransactorId)}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3 border-bottom pb-3">
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Amount</Label>
                      <div className={`fw-bold fs-5 ${(detailEntry.debit || detailEntry.Debit) > 0 ? "text-success" : "text-danger"}`}>
                        {formatCurrency((detailEntry.debit || detailEntry.Debit) || (detailEntry.credit || detailEntry.Credit))}
                      </div>
                    </Col>
                    <Col md={6}>
                      <Label className="text-muted small mb-1 d-block">Cost Head</Label>
                      <div className="fw-bold">{getCostHeadName(detailEntry.costHeadId || detailEntry.CostHeadId)}</div>
                    </Col>
                  </Row>

                  <div className="mb-0">
                    <Label className="text-muted small mb-1 d-block">Notes</Label>
                    <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', minHeight: '80px', fontSize: '0.9rem' }}>
                      {detailEntry.notes || detailEntry.Notes || "No notes provided"}
                    </div>
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter className="border-0">
               <Button color="secondary" onClick={() => setShowDetailModal(false)}>Close</Button>
            </ModalFooter>
          </Modal>

          {/* Edit Modal */}
          <Modal isOpen={showEditModal} toggle={() => !saveLoading && setShowEditModal(false)} size="lg" centered>
            <ModalHeader toggle={() => !saveLoading && setShowEditModal(false)}>
              Edit Petty Cash Entry
            </ModalHeader>
            <ModalBody>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Amount <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        value={editForm.Amount}
                        onChange={(e) => setEditForm(prev => ({ ...prev, Amount: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                   <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Transactor Type <span className="text-danger">*</span></Label>
                      <Select
                        value={transactorTypeOptions.find(t => t.value === editForm.TransactorType) || null}
                        onChange={(opt) => handleTransactorTypeChange(opt?.value, editIsReceipt, true)}
                        options={transactorTypeOptions}
                        styles={selectStyles}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                   <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Transactor Selection <span className="text-danger">*</span></Label>
                       <Select
                          value={editTransactorOptions.find(o => o.value === editForm.TransactorId) || null}
                          onChange={(opt) => setEditForm(prev => ({ ...prev, TransactorId: opt?.value }))}
                          options={editTransactorOptions}
                          styles={selectStyles}
                        />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Cost Head</Label>
                      <Select
                         value={costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === editForm.CostHeadId) 
                            ? { value: editForm.CostHeadId, label: costHeads.find(c => (c.costHead_Id || c.CostHead_Id) === editForm.CostHeadId).costHead_Name } 
                            : null}
                        onChange={(opt) => setEditForm(prev => ({ ...prev, CostHeadId: opt?.value }))}
                        options={costHeads.map(c => ({ value: c.costHead_Id || c.CostHead_Id, label: c.costHead_Name || c.CostHead_Name }))}
                        styles={selectStyles}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>
                 <FormGroup className="mb-3">
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={editForm.Notes}
                      onChange={(e) => setEditForm(prev => ({ ...prev, Notes: e.target.value }))}
                    />
                  </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowEditModal(false)} disabled={saveLoading}>Cancel</Button>
              <Button color="primary" onClick={handleUpdate} disabled={saveLoading}>
                {saveLoading ? <ClipLoader size={15} color="#fff" /> : "Save Changes"}
              </Button>
            </ModalFooter>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
}
